import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { LiveQueue } from '@arcnet/contracts';
import { useLiveState } from './useLiveState.js';
import { useSession } from '../../lib/auth/session.js';
import { useTenants } from '../../lib/api/hooks.js';
import { Badge, Empty, Stat, StatGroup } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration, formatNumber } from '../../lib/format.js';

/**
 * Every queue on the platform, as it stands this second.
 *
 * The counterpart to Queue performance, not a replacement for it: that report
 * answers "did we answer the phone last week" from the event history, this
 * answers "is anybody waiting right now". Keeping them apart is what lets each
 * one be honest — the history can count answered calls correctly, and this can
 * refuse to.
 *
 * Updates arrive on the queues channel. mod_callcenter's own events tell the
 * node when to re-read a queue, and the node pushes the moment it differs, so a
 * caller joining shows up here within a second. The wait then counts up in the
 * browser from when the caller joined: a caller merely waiting sends nothing.
 */

/**
 * Seconds the longest-waiting caller has waited, at `now` on the switch's clock.
 *
 * An agent from before 15 September 2026 sends no join time; its
 * longest_wait_sec is as of the last push, which beats showing nothing.
 */
function waitSec(q: LiveQueue, now: number): number {
  if (q.longest_waiting_since == null) return q.waiting > 0 ? q.longest_wait_sec : 0;
  return Math.max(0, Math.floor((now - q.longest_waiting_since) / 1000));
}

/**
 * Waiting callers first, then whoever has waited longest — the two reasons to
 * look. By join time rather than elapsed seconds, so rows do not reshuffle on
 * every tick of the clock.
 */
function bySeverity(a: LiveQueue, b: LiveQueue): number {
  return b.waiting - a.waiting
    || (a.longest_waiting_since ?? Infinity) - (b.longest_waiting_since ?? Infinity)
    || b.longest_wait_sec - a.longest_wait_sec
    || a.name.localeCompare(b.name);
}

/**
 * How worried to look about a wait.
 *
 * Thresholds rather than a gradient: a supervisor is scanning for the one row
 * that needs them, and five shades of amber is not a signal.
 */
function waitTone(sec: number): 'ok' | 'warn' | 'danger' {
  if (sec >= 120) return 'danger';
  if (sec >= 30) return 'warn';
  return 'ok';
}

export function LiveQueuesPage() {
  const nav = useNavigate();
  const { queues, connection, streamed, nodes, skewMs } = useLiveState(['queues']);
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const platform = session?.scope.all_tenants === true;
  const [onlyBusy, setOnlyBusy] = useState(false);

  /**
   * A one-second display clock, running only while somebody waits.
   *
   * The push says when the caller joined, not how long they have waited, so an
   * idle wallboard does no work at all and a busy one does one render a second.
   */
  const someoneWaiting = queues.some((q) => q.longest_waiting_since != null);
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (!someoneWaiting) return;
    setClock(Date.now());
    const t = setInterval(() => setClock(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [someoneWaiting]);
  const now = clock + skewMs;

  /**
   * SIP domain to tenant id.
   *
   * The tenant list is already in the query cache for any operator (the tenant
   * switcher loads it), so this is free in the common case. A tenant user has
   * exactly one account and never needs the lookup.
   */
  const tenants = useTenants({ limit: 200 });
  const byDomain = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants.data?.data ?? []) m.set(t.sip_domain, t.id);
    // A tenant-scoped user sees only their own queues; the switcher list is not
    // loaded for them, so fall back to the account they are already in.
    if (!platform && activeTenantId) {
      for (const q of queues) m.set(q.domain, activeTenantId);
    }
    return m;
  }, [tenants.data, queues, platform, activeTenantId]);

  const rows = useMemo(() => {
    const list = onlyBusy ? queues.filter((q) => q.waiting > 0) : [...queues];
    return list.sort(bySeverity);
  }, [queues, onlyBusy]);

  const totals = useMemo(() => ({
    queues: queues.length,
    waiting: queues.reduce((n, q) => n + q.waiting, 0),
    ready: queues.reduce((n, q) => n + q.agents_available, 0),
    onCall: queues.reduce((n, q) => n + q.agents_on_call, 0),
    staffed: queues.reduce((n, q) => n + q.agents_total, 0),
  }), [queues]);
  const longest = queues.reduce((n, q) => Math.max(n, waitSec(q, now)), 0);

  /**
   * Queues with callers but nobody able to take them.
   *
   * The single most useful thing this page can say, and it is not visible from
   * any one column: waiting > 0 with no available agent is a queue that is not
   * going to clear on its own.
   */
  const unstaffed = useMemo(
    () => rows.filter((q) => q.waiting > 0 && q.agents_available === 0), [rows]);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Queues</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Callers waiting and agents available, right now
          </p>
        </div>
        <Badge tone={connection === 'live' ? 'ok' : 'warn'}>
          <span className={connection === 'live' ? 'arc-dot' : 'arc-dot arc-pulse'} aria-hidden="true" />
          {connection === 'live' ? (streamed ? 'live' : 'waiting for data') : connection}
        </Badge>
      </div>

      <StatGroup>
        <Stat bare label="Queues" value={formatNumber(totals.queues)} icon="queue" hue={1}
          sub={platform ? 'across the platform' : 'in this account'} />
        <Stat bare label="Waiting" value={formatNumber(totals.waiting)} icon="clock" hue={2}
          tone={totals.waiting > 0 ? 'warn' : undefined}
          sub={totals.waiting === 0 ? 'nobody in a queue' : 'callers in a queue'} />
        <Stat bare label="Longest wait" value={formatDuration(longest)} icon="warning" hue={3}
          tone={longest >= 120 ? 'danger' : longest >= 30 ? 'warn' : undefined}
          sub="worst caller right now" />
        <Stat bare label="Agents ready" value={formatNumber(totals.ready)} icon="users" hue={4}
          tone={totals.waiting > 0 && totals.ready === 0 ? 'danger' : undefined}
          sub={`${formatNumber(totals.onCall)} on a call · ${formatNumber(totals.staffed)} staffed`} />
      </StatGroup>

      {/*
        Nothing on the switch side has reported recently.
        Every number below is then a leftover from before the feed stopped, and
        a wallboard showing an old "0 waiting" is worse than one showing
        nothing: somebody will walk past it and conclude the queue is clear.
      */}
      {nodes === 0 && (
        <div className="arc-banner arc-banner-warn" style={{ margin: '14px 0' }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>No node is reporting</div>
            <div style={{ opacity: .85, marginTop: 2 }}>
              These figures are the last ones received and are not current.
            </div>
          </div>
        </div>
      )}

      {unstaffed.length > 0 && (
        <div className="arc-banner arc-banner-danger" style={{ margin: '14px 0' }} role="alert">
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {unstaffed.length === 1
                ? `${unstaffed[0]!.name} has callers waiting and no agent available`
                : `${unstaffed.length} queues have callers waiting and no agent available`}
            </div>
            <div style={{ opacity: .85, marginTop: 2 }}>
              Nothing is going to answer these calls until somebody becomes available.
            </div>
          </div>
        </div>
      )}

      <div className="arc-card" style={{ marginTop: unstaffed.length > 0 ? 0 : 14 }}>
        <div className="arc-toolbar">
          <div className="arc-row" style={{ gap: 4 }} role="group" aria-label="Filter queues">
            <button type="button"
              className={`arc-btn arc-btn-sm ${onlyBusy ? 'arc-btn-ghost' : 'arc-btn-primary'}`}
              onClick={() => setOnlyBusy(false)}>All queues</button>
            <button type="button"
              className={`arc-btn arc-btn-sm ${onlyBusy ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
              onClick={() => setOnlyBusy(true)}>With callers waiting</button>
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle" style={{ fontSize: 12.5 }}>
            {rows.length === queues.length ? `${queues.length}` : `${rows.length} of ${queues.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col />
              <col style={{ width: 110 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 210 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Queue</th>
                <th className="arc-td-num">Waiting</th>
                <th className="arc-td-num">Longest wait</th>
                <th>Agents</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.queue}>
                  <td>
                    <div className="arc-stack">
                      <span style={{ fontWeight: 550 }}>{q.name}</span>
                      {platform && (
                        <span className="arc-subtle" style={{ fontSize: 11 }}>{q.domain}</span>
                      )}
                    </div>
                  </td>
                  <td className="arc-td-num">
                    {q.waiting > 0
                      ? <Badge tone={q.agents_available === 0 ? 'danger' : 'warn'}>
                          {formatNumber(q.waiting)}
                        </Badge>
                      : <span className="arc-subtle">—</span>}
                  </td>
                  <td className="arc-td-num">
                    {q.waiting > 0
                      ? <span style={{ color: `var(--arc-${waitTone(waitSec(q, now))})`, fontWeight: 600 }}>
                          {formatDuration(waitSec(q, now))}
                        </span>
                      : <span className="arc-subtle">—</span>}
                  </td>
                  <td>
                    {/* Ready, busy and total in one cell. Three columns of small
                        integers read as a spreadsheet; the sentence is what a
                        supervisor is actually asking. */}
                    <span className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
                      <span style={{
                        fontWeight: 600,
                        color: q.agents_available === 0 && q.waiting > 0
                          ? 'var(--arc-danger)' : undefined,
                      }}>
                        {formatNumber(q.agents_available)} ready
                      </span>
                      <span className="arc-subtle">
                        · {formatNumber(q.agents_on_call)} on a call
                        · {formatNumber(q.agents_total)} staffed
                      </span>
                    </span>
                  </td>
                  {/* The live payload identifies a queue by SIP domain, not by
                      tenant id, so the drill-through resolves the domain against
                      the tenant list. Rendered only when that succeeds -- a
                      button that silently does nothing is worse than no button. */}
                  <td className="arc-td-right">
                    {byDomain.get(q.domain) ? (
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                        title={`Queue performance for ${q.name}`}
                        onClick={() => void nav({
                          to: '/manage/$tenantId/$',
                          params: { tenantId: byDomain.get(q.domain)!, _splat: 'queue-reports' },
                        })}>
                        History
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {queues.length === 0 && (
          <Empty icon="queue"
            title={streamed ? 'No queues are running' : 'Waiting for the switch'}
            hint={streamed
              ? 'A queue appears here as soon as it has agents assigned to it.'
              : 'Connecting to the live event stream.'} />
        )}
        {queues.length > 0 && rows.length === 0 && (
          <Empty icon="check" title="Nobody is waiting"
            hint="Every queue is clear right now." />
        )}
      </div>

      {/*
        Answered counts are deliberately absent.
        mod_callcenter's per-agent `calls_answered` is a LIFETIME counter, and
        an agent tiered into three queues contributes their whole total to all
        three -- so summing it per queue produces a number that is both stale
        and double-counted. The queue performance report derives answered counts
        from the event history, where they are correct. A wrong number on a
        wallboard is worse than no number, because somebody will act on it.
      */}
      <p className="arc-muted" style={{ fontSize: 12, marginTop: 12 }}>
        Answered and abandoned counts live in <strong>Queue performance</strong>, which derives
        them from the queue event history. The switch's own counters are per agent and
        cumulative, so they cannot be attributed to one queue.
      </p>
    </>
  );
}

